/**
 * 견적 메뉴 단일 정의표 (헌법)
 *
 * 모든 신규 생성 진입점(PC 모달 / 모바일 FAB / topTab별 헤더 버튼)이
 * 이 파일을 단일 소스로 참조한다.
 *
 * 신규 견적 종류 추가: ESTIMATE_KINDS에 객체 1개 추가
 * deprecated 처리: enabled: false
 * 라벨/라우트 변경: 이 파일만 수정
 *
 * 라우트 자체는 App.jsx에서 별도 정의되며 본 헌법은 메뉴 노출만 담당.
 * deprecated 견적도 라우트는 살아있어서 URL 직접 접근 가능.
 */

/**
 * 견적 종류 정의.
 * key: 내부 식별자 (영문, kebab 또는 camel)
 * label: 사용자 노출 라벨 (한국어)
 * shortLabel: 모바일 FAB 등 좁은 영역 라벨 (선택, 없으면 label 사용)
 * path: 라우트 path
 * tab: 어느 topTab에 속하는지 (general / simple / franchise / balance / all)
 *      all 탭에서는 enabled: true 모두 노출
 *      특정 탭에서는 그 탭에 속한 enabled: true만 노출
 * enabled: 메뉴 노출 여부 (false면 라우트는 살아있되 메뉴에서 숨김)
 * chooser: true이면 모달 안에서 추가 선택 단계 거침 (예: 프랜차이즈 → 브랜드 선택)
 * description: 설명 (모달 등에서 사용 가능)
 */
export const ESTIMATE_KINDS = [
  // 활성 견적
  {
    key: 'quick',
    label: '가견적',
    shortLabel: '가견적',
    path: '/estimate/quick',
    tab: 'general',
    enabled: true,
    description: '채팅 기반 빠른 견적',
  },
  {
    key: 'manual',
    label: '수동견적',
    shortLabel: '수동',
    path: '/estimate/manual',
    tab: 'general',
    enabled: true,
    description: '엑셀처럼 직접 작성 (빈 양식 / 견적 복사)',
  },
  {
    key: 'simple',
    label: '간이견적',
    shortLabel: '간이',
    path: '/estimate/simple',
    tab: 'simple',
    enabled: true,
    description: '항목 직접 입력 견적',
  },
  {
    key: 'franchise',
    label: '프랜차이즈',
    shortLabel: '프차',
    path: '/brands',
    tab: 'franchise',
    enabled: true,
    chooser: true,
    description: '브랜드 선택 후 견적 생성',
  },
  {
    key: 'balance',
    label: '잔금내역서',
    shortLabel: '잔금',
    path: '/balance/new',
    tab: 'balance',
    enabled: true,
    description: '잔금 내역 관리',
  },

  // Deprecated — 메뉴 미노출, 라우트는 유지 (URL 직접 접근 / 행 클릭으로만 진입)
  {
    key: 'quickLegacy',
    label: '(구)가견적',
    shortLabel: '(구)가견적',
    path: '/estimate/quick-legacy',
    tab: 'general',
    enabled: false,
    description: '(구) 가견적 — 데이터 참조 전용',
  },
  {
    key: 'full',
    label: '본견적',
    shortLabel: '본견적',
    path: '/estimate/new',
    tab: 'general',
    enabled: false,
    description: '(구) 본견적 — 데이터 참조 전용',
  },
  {
    key: 'v2Beta',
    label: 'v2 (beta)',
    shortLabel: 'v2',
    path: '/estimate/v2',
    tab: 'general',
    enabled: false,
    description: '(구) v2 beta — 데이터 참조 전용',
  },
  {
    key: 'simpleV2',
    label: '간이 v2 (beta)',
    shortLabel: '간이 v2',
    path: '/estimate/simple-v2',
    tab: 'simple',
    enabled: false,
    description: '(구) 간이 v2 beta — 데이터 참조 전용',
  },
];

/**
 * topTab 정의 — 5개 탭의 라벨/필터 기준
 */
export const TOP_TABS = [
  // 전체(all) 탭 숨김(2026-06 SB): 로직/데이터는 보존, 메뉴 노출만 제거.
  { key: 'franchise', label: '프랜차이즈', shortLabel: '프차' },
  { key: 'general',   label: '상세견적',   shortLabel: '상세' },
  { key: 'simple',    label: '간이견적',   shortLabel: '간이' },
  { key: 'balance',   label: '잔금내역서', shortLabel: '잔금' },
];

/**
 * 헬퍼 — 메뉴 노출용 견적 종류 필터링
 *
 * @param {string} topTab - 현재 활성 탭 ('all' | 'general' | 'simple' | ...)
 * @returns {Array} 해당 탭에서 노출할 견적 종류 (enabled: true만)
 *
 * 사용 예:
 *   const options = getEnabledKindsForTab('all');
 *   const generalOptions = getEnabledKindsForTab('general');
 */
export function getEnabledKindsForTab(topTab) {
  const enabled = ESTIMATE_KINDS.filter((k) => k.enabled);
  if (topTab === 'all') return enabled;
  return enabled.filter((k) => k.tab === topTab);
}

/**
 * 헬퍼 — key로 견적 종류 객체 조회 (deprecated 포함)
 *
 * @param {string} key - 견적 종류 key
 * @returns 견적 종류 객체 또는 undefined
 */
export function getKindByKey(key) {
  return ESTIMATE_KINDS.find((k) => k.key === key);
}

/**
 * 헬퍼 — path로 견적 종류 key 역조회 (행 클릭 시 사용 가능)
 *
 * @param {string} path - 라우트 path (예: /estimate/quick-legacy)
 * @returns 견적 종류 객체 또는 undefined
 */
export function getKindByPath(path) {
  return ESTIMATE_KINDS.find((k) => path.startsWith(k.path));
}
